import { createFileRoute } from "@tanstack/react-router";

import { useI18n } from "~/lib/i18n";
import { developerMessages } from "~/lib/i18n-developers";
import { ui } from "~/lib/ui";

export const Route = createFileRoute("/developers")({
  component: Developers,
});

// The api serves the spec it is generated from (restapi.SpecPath), so this can't go stale.
const specUrl = "/api/v0/openapi.yaml";

type Words = (typeof developerMessages)["en"];

// The booking calls in Beckn's order (docs/02-poc-plan.md §3.2).
const bookingCalls: { step: string; call: string; does: keyof Words }[] = [
  { step: "search", call: "GET /api/v0/trips", does: "doesSearch" },
  { step: "select", call: "GET /api/v0/trips/{tripId}/seats", does: "doesSelect" },
  { step: "init", call: "POST /api/v0/holds", does: "doesInit" },
  { step: "confirm", call: "POST /api/v0/bookings", does: "doesConfirm" },
  { step: "status", call: "GET /api/v0/tickets/{ticketId}", does: "doesStatus" },
  { step: "cancel", call: "POST /api/v0/tickets/{ticketId}/cancel", does: "doesCancel" },
];

const otherCalls: { title: keyof Words; call: string; body: keyof Words }[] = [
  { title: "fareLookup", call: "GET /api/v0/fares/quote", body: "fareLookupBody" },
  { title: "vehicleCheck", call: "GET /api/v0/verify/{stickerCode}", body: "vehicleCheckBody" },
  { title: "webhooks", call: "ticket.confirmed · ticket.cancelled · ticket.boarded", body: "webhooksBody" },
];

// Long paths may break anywhere rather than widen a phone screen.
const code = "font-mono text-[0.8125rem] [overflow-wrap:anywhere]";

/** What the partner API is and how a licensed channel starts using it. */
function Developers() {
  const { t, lang } = useI18n();
  const w = developerMessages[lang];

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-slate-600 dark:text-slate-400">{t("systemName")}</p>
        <h1 className={`mt-1 ${ui.h1}`}>{t("partnerApi")}</h1>
        <p className={ui.lead}>{w.lead}</p>
      </div>

      <section className="space-y-2">
        <h2 className={ui.h2}>{w.whatTitle}</h2>
        <p>{w.what1}</p>
        <p>{w.what2}</p>
      </section>

      <section className={`${ui.card} space-y-3`}>
        <h2 className={ui.h2}>{w.specTitle}</h2>
        <p>{w.specBody}</p>
        <a href={specUrl} download="openapi.yaml" className={`inline-block ${ui.primary}`}>
          {w.specDownload}
        </a>
        <p className={ui.muted}>{w.specNotes}</p>
      </section>

      <section className="space-y-3">
        <h2 className={ui.h2}>{w.bookingTitle}</h2>
        <p>{w.bookingBody}</p>
        <div className={ui.tableWrap}>
          <table className={ui.table}>
            <thead className={ui.thead}>
              <tr>
                <th className={ui.th}>{w.colStep}</th>
                <th className={ui.th}>{w.colCall}</th>
              </tr>
            </thead>
            <tbody className={ui.tbody}>
              {bookingCalls.map((c) => (
                <tr key={c.step}>
                  <td className={`${ui.cell} font-medium`}>{c.step}</td>
                  <td className={ui.cell}>
                    <code className={code}>{c.call}</code>
                    <p className="mt-0.5 text-slate-600 dark:text-slate-400">{w[c.does]}</p>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className={ui.h2}>{w.moreTitle}</h2>
        <dl className="space-y-4">
          {otherCalls.map((c) => (
            <div key={c.title}>
              <dt className="font-semibold">{w[c.title]}</dt>
              <dd className={`${code} mt-0.5 text-slate-700 dark:text-slate-300`}>{c.call}</dd>
              <dd className="mt-1">{w[c.body]}</dd>
            </div>
          ))}
        </dl>
      </section>

      <div className="grid gap-8 sm:grid-cols-2">
        <section className="space-y-2">
          <h2 className={ui.h2}>{w.keysTitle}</h2>
          <p>{w.keys1}</p>
          <p>{w.keys2}</p>
        </section>
        <section className="space-y-2">
          <h2 className={ui.h2}>{w.limitsTitle}</h2>
          <p>{w.limits1}</p>
          <p>{w.limits2}</p>
        </section>
      </div>

      <section className="space-y-2">
        <h2 className={ui.h2}>{w.exampleTitle}</h2>
        <p>{w.exampleBody}</p>
        {/* A separate app, so a full page load rather than the router. */}
        <a href="/wallet/" className={ui.link}>
          {w.exampleLink}
        </a>
      </section>
    </div>
  );
}
